/**
 * ENG-9466 — the `description:` frontmatter of the generated
 * `.claude/skills/core-knowledge/SKILL.md`.
 *
 * This string is NOT on-demand content. A skill's name and description are
 * resident in context for the whole session — that is how the model decides
 * whether to invoke the skill at all. The generator used to interpolate every
 * knowledge title into it, uncapped, which put the entire index permanently in
 * the window a SECOND time (the first copy being CLAUDE.md's `## Core
 * Knowledge`, removed by the same issue).
 *
 * Measured on the prod fleet before this change: the largest agent's
 * description was 92,828 characters, on top of a 134,208-character CLAUDE.md —
 * 227,036 characters, ~63,600 tokens, ~32% of a 200k window, spent before the
 * agent read anything. Four agents shared that shape and the top nine all
 * carried a description over 33,000 characters.
 *
 * The cap is 1,024 characters because that is the documented Agent Skills
 * limit for a skill description. We do not know whether Claude Code truncates
 * or rejects beyond it — which is the point: an unbounded description is
 * either silently truncated (so the tail titles were never doing the discovery
 * work they appeared to) or fully resident (so it is enormously expensive).
 * Both readings argue for staying inside the documented bound, and neither
 * requires us to guess which one is true.
 */
export const CORE_KNOWLEDGE_DESCRIPTION_MAX_CHARS = 1024;

/**
 * Per-title cap. Titles are written by users as mini-abstracts, not names —
 * the longest observed in prod ran to several hundred characters — so one
 * entry could otherwise consume the whole budget and starve every other topic
 * of a trigger keyword.
 */
export const CORE_KNOWLEDGE_TITLE_MAX_CHARS = 80;

const HEAD =
  'Use this skill when the user asks about the company, organization, team, products, or any of: ';
const TAIL = '. Provides core reference material from the knowledge base.';

/**
 * Room for the remainder marker.
 *
 * The marker NAMES THE RECOVERY PATH rather than just counting what was cut.
 * Field feedback from agents on the fleet: they lean on this skill auto-firing
 * and rarely reach for the search tools, and `knowledge_search` matches title
 * or slug ONLY — so an agent that cannot see a title in context cannot search
 * for it either. A bare "and N more topics" is a dead end that says something
 * exists and gives no way to reach it. Pointing at `knowledge_list` (which
 * returns every id, title and scope on demand) turns the truncation into a
 * signpost, and costs ~55 characters of the 1,024 budget.
 */
const MARKER_RESERVE = 110;

/**
 * Flatten a knowledge title into a trigger keyword: no newlines, no quoting
 * characters that would fight the JSON/YAML encoding, collapsed whitespace,
 * lower-cased, and length-bounded.
 */
export function normalizeKnowledgeTitle(title: string): string {
  const flat = title
    .replace(/[\n\r"\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  if (flat.length <= CORE_KNOWLEDGE_TITLE_MAX_CHARS) return flat;
  return `${flat.slice(0, CORE_KNOWLEDGE_TITLE_MAX_CHARS - 1).trimEnd()}…`;
}

/**
 * Build the skill description from the agent's knowledge titles, bounded by
 * {@link CORE_KNOWLEDGE_DESCRIPTION_MAX_CHARS}.
 *
 * Titles are included greedily in the order given, so callers control which
 * topics get a literal trigger keyword. Whatever does not fit is accounted for
 * explicitly rather than dropped silently — the agent is told a remainder
 * exists, and the skill body still carries every entry in full.
 */
export function buildCoreKnowledgeDescription(titles: readonly string[]): string {
  const normalized = titles.map(normalizeKnowledgeTitle).filter(Boolean);
  if (normalized.length === 0) {
    return 'Use this skill for core reference material from the knowledge base.';
  }

  const budget =
    CORE_KNOWLEDGE_DESCRIPTION_MAX_CHARS - HEAD.length - TAIL.length - MARKER_RESERVE;

  const chosen: string[] = [];
  let used = 0;
  for (const title of normalized) {
    const cost = (chosen.length > 0 ? 2 : 0) + title.length;
    if (used + cost > budget) break;
    chosen.push(title);
    used += cost;
  }

  // A single title wider than the whole budget still has to yield something
  // usable; CORE_KNOWLEDGE_TITLE_MAX_CHARS makes this unreachable today, but
  // an empty enumeration would be a worse failure than a clipped one.
  if (chosen.length === 0) {
    chosen.push((normalized[0] ?? '').slice(0, Math.max(1, budget)));
  }

  const dropped = normalized.length - chosen.length;
  const marker =
    dropped > 0 ? `, and ${dropped} more (call knowledge_list for the full index)` : '';
  return `${HEAD}${chosen.join(', ')}${marker}${TAIL}`;
}

/**
 * Order knowledge titles for the (capped) description.
 *
 * ENG-9466 follow-up. Capping the description turned an ordering that had
 * never mattered into the one that decides almost everything: with every title
 * included, the order the API happened to return was irrelevant; at ~9 of 525
 * it IS the agent's entire trigger vocabulary.
 *
 * `/host/refresh` returns the scopes separately and concatenates them
 * org-then-team-then-global, so a straight greedy take spends the whole budget
 * inside the FIRST scope and later scopes get no keyword at all. Measured on a
 * knowledge-heavy shape (40 org + 200 team, verbose titles): 9 org keywords,
 * 0 team — the live, actively-used knowledge was the half that vanished.
 *
 * Round-robin instead, so every scope the agent actually has is represented
 * before any scope takes a second slot. Within a scope the caller's order is
 * preserved, which is `created_at DESC` — the most recently curated entries
 * win. That is a proxy, not relevance ranking; genuine ranking needs usage
 * counts or an operator-pinned flag and is deliberately not attempted here.
 */
export function orderTitlesForDescription(
  entries: readonly { title: string; scope: string }[],
): string[] {
  // Insertion order of the Map is first-appearance order, so the caller keeps
  // control of which scope gets the first slot.
  const byScope = new Map<string, string[]>();
  for (const entry of entries) {
    const bucket = byScope.get(entry.scope);
    if (bucket) bucket.push(entry.title);
    else byScope.set(entry.scope, [entry.title]);
  }

  const buckets = [...byScope.values()];
  const ordered: string[] = [];
  for (let rank = 0; ordered.length < entries.length; rank++) {
    let progressed = false;
    for (const bucket of buckets) {
      const title = bucket[rank];
      if (title !== undefined) {
        ordered.push(title);
        progressed = true;
      }
    }
    // Guard against a non-terminating loop if a bucket is somehow shorter than
    // the count implies; `ordered.length < entries.length` alone is not enough.
    if (!progressed) break;
  }
  return ordered;
}
